import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { t } from '../../i18n';
import { cn } from '../../lib/utils';
import { focusNextEnterNav, handleEnterNavKeyDown } from '../../lib/focusEnterNav';
import { ChevronDownIcon, XIcon } from './Icons';

export interface SelectOption {
  value: string | number;
  label: string;
  /** Optional icon shown before the label in trigger and dropdown */
  icon?: React.ReactNode;
  /** Secondary line (muted) — used in dropdown; trigger shows label + short description when set */
  description?: string;
  /** Optional trailing content (e.g. count badge) on the right of the option row */
  trailing?: React.ReactNode;
}

export interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name?: string;
  value?: string | number;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  /** Called when the dropdown closes (after select, clear, or click outside). Receives current value. */
  onClose?: (value: string | number) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  showClearButton?: boolean;
  error?: string;
  helperText?: string;
  /** When true, Enter moves focus to the next field marked with data-enter-nav. */
  enterNav?: boolean;
  /** Compact trigger height (single-line). */
  size?: 'sm' | 'default';
  /** Extra classes for the visible trigger control. */
  triggerClassName?: string;
}

const isPrintableKey = (key: string) => key.length === 1 && !key.startsWith('Dead');

const Select: React.FC<SelectProps> = ({
  name,
  value,
  onChange,
  onClose,
  options = [],
  placeholder = t().common.placeholders.select,
  searchPlaceholder = t().common.placeholders.searchType,
  disabled = false,
  readOnly = false,
  required = false,
  showClearButton = true,
  error,
  helperText,
  enterNav = false,
  size = 'default',
  triggerClassName,
  className,
  ...props
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownRect, setDropdownRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  /** When true, highlightedIndex changed via keyboard — listbox may auto-scroll. */
  const highlightFromKeyboardRef = useRef(false);

  // Normalize so we always have an array of { value, label } and can display when options load
  const normalizedOptions: SelectOption[] = Array.isArray(options)
    ? options.map((o) => ({
        value: o?.value ?? '',
        label: typeof o?.label === 'string' ? o.label : String(o?.value ?? ''),
        icon: o?.icon,
        description: typeof o?.description === 'string' ? o.description : undefined,
        trailing: o?.trailing,
      }))
    : [];

  const valueStr = value !== undefined && value !== null ? String(value) : '';
  const selectedOption = normalizedOptions.find((o) => String(o.value) === valueStr);
  const hasValue = selectedOption !== undefined;

  const q = searchQuery.trim().toLowerCase();
  const filteredOptions = q
    ? normalizedOptions.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.description && o.description.toLowerCase().includes(q))
      )
    : normalizedOptions;

  const updateDropdownRect = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      const horizontalPad = 16;
      const gap = 6;
      // Search row (~52) + option list (max-h-52 = 208)
      const preferredPanelHeight = 260;
      const maxPanel = Math.min(vw - horizontalPad * 2, 36 * 16);
      // At least as wide as the trigger; ensure room for label + description rows; cap to viewport
      const width = Math.min(Math.max(rect.width, 280), maxPanel);
      let left = rect.left;
      if (left + width > vw - horizontalPad) {
        left = Math.max(horizontalPad, vw - horizontalPad - width);
      }
      const spaceBelow = Math.max(0, vh - rect.bottom - gap);
      const spaceAbove = Math.max(0, rect.top - gap);
      const openUp = spaceBelow < preferredPanelHeight && spaceAbove > spaceBelow;
      const available = openUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(140, Math.min(preferredPanelHeight, available));
      if (openUp) {
        setDropdownRect({
          bottom: vh - rect.top + gap,
          left,
          width,
          maxHeight,
          placement: 'top',
        });
      } else {
        setDropdownRect({
          top: rect.bottom + gap,
          left,
          width,
          maxHeight,
          placement: 'bottom',
        });
      }
    }
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  }, []);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
    setHighlightedIndex(-1);
    updateDropdownRect();
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
  }, []);

  const selectOption = useCallback((option: SelectOption) => {
    if (disabled || readOnly) return;
    const newVal = String(option.value);
    onChange?.({ target: { name, value: newVal, type: 'select-one' } });
    setSearchQuery('');
    setHighlightedIndex(-1);
    setIsOpen(false);
    if (onClose) onClose(newVal);
    if (enterNav) {
      requestAnimationFrame(() => focusNextEnterNav(containerRef.current));
    }
  }, [disabled, readOnly, onChange, name, onClose, enterNav]);

  const moveHighlight = useCallback((direction: 1 | -1) => {
    if (filteredOptions.length === 0) {
      highlightFromKeyboardRef.current = false;
      setHighlightedIndex(-1);
      return;
    }
    highlightFromKeyboardRef.current = true;
    setHighlightedIndex((current) => {
      if (current < 0) {
        return direction === 1 ? 0 : filteredOptions.length - 1;
      }
      return (current + direction + filteredOptions.length) % filteredOptions.length;
    });
  }, [filteredOptions.length]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveHighlight(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveHighlight(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          selectOption(filteredOptions[highlightedIndex]);
        } else if (filteredOptions.length === 1) {
          selectOption(filteredOptions[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
      case 'Tab':
        closeDropdown();
        break;
      case 'Home':
        e.preventDefault();
        if (filteredOptions.length > 0) {
          highlightFromKeyboardRef.current = true;
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        e.preventDefault();
        if (filteredOptions.length > 0) {
          highlightFromKeyboardRef.current = true;
          setHighlightedIndex(filteredOptions.length - 1);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // Capture phase so outside-click still works inside Modal (which may
    // stopPropagation on bubble-phase click handlers).
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [closeDropdown]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownRect(null);
      return;
    }

    updateDropdownRect();
    const onScrollOrResize = (event?: Event) => {
      if (event?.type === 'scroll' && dropdownRef.current?.contains(event.target as Node)) {
        return;
      }
      updateDropdownRect();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [searchQuery, isOpen, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0 || !highlightFromKeyboardRef.current) return;
    highlightFromKeyboardRef.current = false;
    optionRefs.current.get(highlightedIndex)?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, isOpen, filteredOptions]);

  useEffect(() => {
    if (!isOpen || disabled || readOnly) return;

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target === searchInputRef.current) return;
      if (dropdownRef.current?.contains(target ?? null)) return;

      if (isPrintableKey(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        searchInputRef.current?.focus();
        setSearchQuery((prev) => prev + event.key);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        searchInputRef.current?.focus();
        moveHighlight(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        searchInputRef.current?.focus();
        moveHighlight(-1);
      } else if (event.key === 'Enter' && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        event.preventDefault();
        selectOption(filteredOptions[highlightedIndex]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
      }
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [
    isOpen,
    disabled,
    readOnly,
    highlightedIndex,
    filteredOptions,
    moveHighlight,
    selectOption,
    closeDropdown,
  ]);

  const handleSelect = (option: SelectOption) => (e: React.MouseEvent) => {
    e.stopPropagation();
    selectOption(option);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    onChange?.({ target: { name, value: '', type: 'select-one' } });
    closeDropdown();
    if (onClose) onClose('');
  };

  const handleTriggerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if ((e.target as HTMLElement).closest('button[type="button"]')) return;
    e.preventDefault();
    if (!isOpen) {
      openDropdown();
    } else {
      searchInputRef.current?.focus();
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (!isOpen && (e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      openDropdown();
      return;
    }
    if (e.key === 'Enter' && !isOpen && enterNav) {
      e.preventDefault();
      focusNextEnterNav(containerRef.current);
    }
  };

  const isDisabled = disabled || readOnly;

  const stopDropdownWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full min-w-0', className)}
      data-enter-nav={enterNav ? '' : undefined}
      {...props}
    >
      <input type="hidden" name={name} value={valueStr} readOnly required={required} />

      <div
        className={cn(
          'flex items-center w-full rounded-lg transition-all duration-200 border select-none',
          size === 'sm' ? 'min-h-8 px-2 py-1 text-xs' : 'min-h-10 px-2.5 py-2 text-sm',
          colors.surface,
          colors.border,
          isOpen && 'ring-2 shadow-sm',
          isOpen && colors.primaryBorder,
          !isOpen && !error && colors.interactiveHover,
          error && 'border-red-500',
          isDisabled && 'opacity-50 cursor-not-allowed',
          isDisabled && colors.surfaceHover,
          !isDisabled && 'cursor-pointer',
          triggerClassName,
        )}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        onMouseDown={handleTriggerMouseDown}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        data-enter-focus={enterNav ? '' : undefined}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className={cn('flex items-center gap-1 flex-1 min-w-0', !hasValue && 'opacity-70')}>
          <ChevronDownIcon
            className={cn(
              'flex-shrink-0 transition-transform duration-200',
              size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5',
              isOpen && 'rotate-180',
              hasValue ? colors.primaryFg : colors.textMuted
            )}
          />
          <div
            className={cn(
              'flex-1 min-w-0 truncate flex flex-col items-start justify-center gap-0',
              size === 'sm' ? 'min-h-0' : 'min-h-[1.25rem]',
            )}
          >
            {hasValue ? (
              <>
                <div className="flex items-center gap-2 min-w-0 w-full">
                  {selectedOption!.icon != null && (
                    <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
                      {selectedOption!.icon}
                    </span>
                  )}
                  <span
                    className={cn(
                      'font-medium truncate min-w-0 flex-1',
                      size === 'sm' ? 'text-xs' : 'text-sm',
                      colors.primaryFg,
                    )}
                  >
                    {selectedOption!.label}
                  </span>
                  {selectedOption!.trailing != null ? (
                    <span className="flex-shrink-0">{selectedOption!.trailing}</span>
                  ) : null}
                </div>
                {size !== 'sm' && selectedOption!.description ? (
                  <span className={cn('text-xs truncate w-full pl-0', colors.textMuted)}>
                    {selectedOption!.description}
                  </span>
                ) : null}
              </>
            ) : (
              <span className={cn(size === 'sm' ? 'text-xs' : 'text-sm', colors.textMuted)}>
                {placeholder}
              </span>
            )}
          </div>
        </div>
        {showClearButton && hasValue && !isDisabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
              size === 'sm' ? 'ml-0.5 p-1' : 'ml-0.5 p-1.5',
              colors.textMuted,
              colors.interactiveHover,
              colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
            )}
            title="Clear"
            tabIndex={-1}
          >
            <XIcon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
          </button>
        )}
      </div>

      {isOpen && !isDisabled && dropdownRect && createPortal(
        <div
          ref={dropdownRef}
          className={cn(
            'fixed z-[9999] rounded-xl shadow-lg border overflow-hidden flex flex-col',
            colors.surface,
            colors.border
          )}
          style={{
            left: dropdownRect.left,
            width: dropdownRect.width,
            maxHeight: dropdownRect.maxHeight,
            ...(dropdownRect.placement === 'top'
              ? { bottom: dropdownRect.bottom }
              : { top: dropdownRect.top }),
          }}
          onWheel={stopDropdownWheel}
        >
          <div className={cn('p-2 border-b shrink-0', colors.border)} onClick={(e) => e.stopPropagation()}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              className={cn(
                'w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-offset-0',
                colors.border,
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                colors.surface,
                colors.border,
                colors.text
              )}
              aria-label="Search options"
              aria-activedescendant={
                highlightedIndex >= 0 ? `select-option-${name ?? 'field'}-${highlightedIndex}` : undefined
              }
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={`select-listbox-${name ?? 'field'}`}
            />
          </div>
          <div
            id={`select-listbox-${name ?? 'field'}`}
            role="listbox"
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain dashboard-scrollbar"
            onWheel={stopDropdownWheel}
          >
            {normalizedOptions.length === 0 ? (
              <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No options</div>
            ) : filteredOptions.length === 0 ? (
              <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No matches</div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = String(option.value) === valueStr;
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={String(option.value)}
                    id={`select-option-${name ?? 'field'}-${index}`}
                    ref={(el) => {
                      if (el) optionRefs.current.set(index, el);
                      else optionRefs.current.delete(index);
                    }}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={handleSelect(option)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => {
                      highlightFromKeyboardRef.current = false;
                      setHighlightedIndex(index);
                    }}
                    style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                    className={cn(
                      'w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center gap-2 select-none',
                      isSelected
                        ? cn(colors.primary, colors.primaryText, 'font-medium')
                        : isHighlighted
                          ? cn(colors.surfaceHover, colors.text, 'ring-1 ring-inset', colors.primaryBorder)
                          : cn(colors.text, colors.interactiveHover)
                    )}
                  >
                    {option.icon != null && (
                      <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4 opacity-80">
                        {option.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 flex flex-col items-start gap-0.5">
                      <span className="w-full leading-snug select-none" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
                        {option.label}
                      </span>
                      {option.description ? (
                        <span
                          className={cn(
                            'w-full text-xs leading-snug font-normal select-none',
                            isSelected ? 'opacity-90' : colors.textMuted
                          )}
                          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                        >
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {option.trailing != null ? (
                      <span
                        className={cn(
                          'flex-shrink-0 self-center',
                          isSelected && '[&_*]:border-white/30 [&_*]:text-white/90',
                        )}
                      >
                        {option.trailing}
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}

      {(error || helperText) && (
        <div className="mt-1 flex flex-col gap-0.5">
          {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
          {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
        </div>
      )}
    </div>
  );
};

Select.displayName = 'Select';

export { Select };
